/**
 * Mobile Bottom Nav
 * Botton mobile navigation. Can be configured
 * to display different items depending if you are
 * in admin view or regular view
 */
import type { IconName } from '@fortawesome/fontawesome-common-types';
import { useLocation } from '@gatsbyjs/reach-router';
import { navigate } from 'gatsby';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Icon from '@components/Icon/Icon';
import UnreadBadge from '@components/UnreadBadge';

import * as ROUTES from '@constants/routes';

import { useUnreadCount } from '@hooks/useUnreadCount';

import { getLocationNameArray } from '@utils/url';

import {
  IconTitle,
  IconWrapper,
  MenuFullWrapper,
} from './MobileBottomMenu.style';

const messages = defineMessages({
  home: {
    id: 'mobileBottomMenu.home',
    defaultMessage: 'Home',
  },
  records: {
    id: 'mobileBottomMenu.records',
    defaultMessage: 'Records',
  },
  contacts: {
    id: 'mobileBottomMenu.contacts',
    defaultMessage: 'Contacts',
  },
  routines: {
    id: 'mobileBottomMenu.routines',
    defaultMessage: 'Routines',
  },
  healthSummary: {
    id: 'mobileBottomMenu.healthSummary',
    defaultMessage: 'Summary',
  },
  report: {
    id: 'mobileBottomMenu.report',
    defaultMessage: 'Report',
  },
  timeline: {
    id: 'mobileBottomMenu.timeline',
    defaultMessage: 'Timeline',
  },
  messenger: {
    id: 'mobileBottomMenu.messenger',
    defaultMessage: 'Messenger',
  },
});

const MENU_OPTIONS: Record<
  string,
  {
    iconName: IconName;
    iconRotation?: number;
    route: string;
    title: { id: string; defaultMessage: string };
  }
> = {
  home: {
    iconName: 'home',
    route: ROUTES.DASHBOARD,
    title: messages.home,
  },
  adminHome: {
    iconName: 'home',
    route: ROUTES.ADMIN_DASHBOARD,
    title: messages.home,
  },
  records: {
    iconName: 'folder-medical',
    route: ROUTES.RECORDS,
    title: messages.records,
  },
  // contacts: {
  //   iconName: 'address-book',
  //   route: ROUTES.CONTACTS,
  //   title: messages.contacts,
  // },
  /*
  routines: {
    iconName: 'alarm-clock',
    route: ROUTES.ROUTINES,
    title: messages.routines,
  },
  */
  messenger: {
    iconName: 'comment',
    route: ROUTES.MESSENGER,
    title: messages.messenger,
  },
  summary: {
    iconName: 'clipboard-medical',
    route: ROUTES.HEALTH_SUMMARY,
    title: messages.healthSummary,
  },
  report: {
    iconName: 'plus',
    route: ROUTES.UPDATE,
    title: messages.report,
  },
  timeline: {
    iconName: 'timeline',
    iconRotation: -90,
    route: ROUTES.TIMELINE,
    title: messages.timeline,
  },
} as const;

export type OptionKeys = keyof typeof MENU_OPTIONS;

const MobileBottomMenu: React.FC<{
  settings: readonly (readonly OptionKeys[])[];
}> = ({ settings = [] }) => {
  const { formatMessage } = useIntl();
  const location = useLocation();
  const { unreadCount, unreadCountLoading } = useUnreadCount();

  const locationNames = getLocationNameArray(location);

  function renderMobileTab(option: OptionKeys) {
    const config = option in MENU_OPTIONS ? MENU_OPTIONS[option] : null;

    if (!config) {
      return null;
    }

    const isRoute = locationNames.includes(config.route.split('/')[1]);

    return (
      <IconWrapper
        key={config.route}
        onClick={() => {
          navigate(config.route);
        }}
      >
        <UnreadBadge
          unreadCount={unreadCount}
          loading={unreadCountLoading}
          color="error"
          show={config.title === messages.messenger}
        >
          <Icon
            clickable
            color="primary.main"
            name={config.iconName}
            rotation={config.iconRotation ?? 0}
            style={{ fontSize: 24 }}
            type={isRoute ? 'fas' : 'fat'}
          />
        </UnreadBadge>
        <IconTitle isSelected={isRoute}>
          {formatMessage(config.title)}
        </IconTitle>
      </IconWrapper>
    );
  }

  return (
    <MenuFullWrapper>
      {settings.map((group) => group.map(renderMobileTab))}
    </MenuFullWrapper>
  );
};

export default MobileBottomMenu;
